import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import NavBar from '../components/navbar';
import AuthModal from '../components/AuthModal';
import TeamWorkflowSVG from '../components/TeamWorkflowSVG';
import WorkflowSVG from '../components/WorkflowSVG';

export default function Home({ onProfileClick, onBillingClick, onSeatsUpdate }) {
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(location.state?.openLogin || false);

  React.useEffect(() => {
    if (location.state?.openLogin) {
      setShowAuthModal(true);
    }
  }, [location.state]);

  const openAuth = () => setShowAuthModal(true);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');

  const styles = {
    page: {
      minHeight: '100vh',
      background:
        'radial-gradient(circle at top left, rgba(34, 197, 94, 0.10), transparent 34%), radial-gradient(circle at 82% 12%, rgba(59, 130, 246, 0.10), transparent 30%), linear-gradient(180deg, #f8fafc 0%, #ffffff 46%, #f7faf8 100%)',
      color: '#102018',
      overflowX: 'hidden',
    },
    shell: {
      width: 'min(1120px, calc(100% - 32px))',
      margin: '0 auto',
    },
    hero: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1.3fr) minmax(240px, 0.7fr)',
      gap: 28,
      alignItems: 'center',
      padding: '24px 0 12px',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 11px',
      borderRadius: 999,
      background: 'rgba(16, 185, 129, 0.10)',
      color: '#047857',
      fontSize: 12,
      fontWeight: 700,
      border: '1px solid rgba(16, 185, 129, 0.18)',
      marginBottom: 10,
    },
    h1: {
      margin: 0,
      maxWidth: 720,
      fontSize: 'clamp(36px, 5vw, 58px)',
      lineHeight: 0.97,
      letterSpacing: '-0.05em',
      color: '#0f172a',
      fontWeight: 850,
    },
    heroText: {
      margin: '10px 0 0',
      maxWidth: 650,
      fontSize: 'clamp(15px, 1.6vw, 18px)',
      lineHeight: 1.5,
      color: '#475569',
    },
    ctaRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
      marginTop: 14,
    },
    primaryButton: {
      border: 0,
      borderRadius: 14,
      padding: '14px 22px',
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      color: '#ffffff',
      fontSize: 16,
      fontWeight: 800,
      cursor: 'pointer',
      boxShadow: '0 14px 30px rgba(5, 150, 105, 0.26)',
    },
    previewCard: {
      position: 'relative',
      borderRadius: 18,
      padding: 9,
      background: 'rgba(255, 255, 255, 0.78)',
      border: '1px solid rgba(148, 163, 184, 0.25)',
      boxShadow: '0 16px 42px rgba(15, 23, 42, 0.12)',
      backdropFilter: 'blur(16px)',
    },
    previewInner: {
      borderRadius: 16,
      background: '#ffffff',
      overflow: 'hidden',
      border: '1px solid #e2e8f0',
    },
    previewTop: {
      padding: '10px 14px',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      gap: 14,
      alignItems: 'center',
      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    },
    address: {
      fontSize: 15,
      color: '#0f172a',
      fontWeight: 850,
      margin: 0,
    },
    statusPill: {
      padding: '6px 10px',
      borderRadius: 999,
      background: '#dcfce7',
      color: '#166534',
      fontSize: 12,
      fontWeight: 800,
      whiteSpace: 'nowrap',
    },
    previewBody: {
      padding: 8,
      display: 'grid',
      gap: 6,
    },
    taskRow: {
      display: 'grid',
      gridTemplateColumns: '28px minmax(0, 1fr) auto',
      gap: 10,
      alignItems: 'center',
      padding: '8px 10px',
      border: '1px solid #edf2f7',
      borderRadius: 12,
      background: '#ffffff',
    },
    taskIcon: {
      width: 28,
      height: 28,
      borderRadius: 9,
      display: 'grid',
      placeItems: 'center',
      background: '#f0fdf4',
      color: '#047857',
      fontSize: 14,
      fontWeight: 900,
    },
    taskTitle: {
      margin: 0,
      fontSize: 14,
      color: '#0f172a',
      fontWeight: 800,
    },
    datePill: {
      fontSize: 12,
      color: '#334155',
      background: '#f1f5f9',
      padding: '7px 9px',
      borderRadius: 999,
      fontWeight: 800,
      whiteSpace: 'nowrap',
    },
    section: {
      padding: '28px 0',
    },
    sectionHeader: {
      textAlign: 'center',
      maxWidth: 760,
      margin: '0 auto 12px',
    },
    eyebrow: {
      margin: '0 0 5px',
      color: '#059669',
      fontSize: 11,
      fontWeight: 850,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    h2: {
      margin: 0,
      fontSize: 'clamp(24px, 3.2vw, 38px)',
      lineHeight: 1.07,
      letterSpacing: '-0.032em',
      color: '#0f172a',
      fontWeight: 850,
    },
    sectionCopy: {
      margin: '7px auto 0',
      maxWidth: 600,
      color: '#64748b',
      fontSize: 14,
      lineHeight: 1.5,
    },
    workflow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: 12,
      marginTop: 10,
    },
    workflowStep: {
      position: 'relative',
      padding: '12px 12px',
      borderRadius: 16,
      background: 'rgba(255, 255, 255, 0.84)',
      border: '1px solid rgba(148, 163, 184, 0.24)',
      boxShadow: '0 8px 22px rgba(15, 23, 42, 0.07)',
    },
    workflowHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 9,
      marginBottom: 8,
    },
    workflowIcon: {
      width: 34,
      height: 34,
      minWidth: 34,
      borderRadius: 11,
      display: 'grid',
      placeItems: 'center',
      background: '#ecfdf5',
      fontSize: 18,
    },
    workflowTitle: {
      margin: 0,
      color: '#0f172a',
      fontSize: 14,
      fontWeight: 850,
      lineHeight: 1.2,
    },
    workflowCopy: {
      margin: '5px 0 0',
      color: '#64748b',
      fontSize: 13,
      lineHeight: 1.45,
      textAlign: 'center',
    },
    split: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 0.88fr) minmax(360px, 1.12fr)',
      gap: 22,
      alignItems: 'center',
      padding: '18px 22px',
      borderRadius: 22,
      background: 'linear-gradient(135deg, #0f172a 0%, #123326 54%, #064e3b 100%)',
      color: '#ffffff',
      boxShadow: '0 16px 44px rgba(15, 23, 42, 0.18)',
    },
    splitH2: {
      margin: 0,
      fontSize: 'clamp(20px, 2.8vw, 32px)',
      lineHeight: 1.08,
      letterSpacing: '-0.03em',
      fontWeight: 850,
    },
    splitCopy: {
      margin: '8px 0 0',
      color: 'rgba(255,255,255,0.78)',
      fontSize: 14,
      lineHeight: 1.5,
    },
    miniStats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 8,
      marginTop: 12,
    },
    miniStat: {
      padding: '9px 10px',
      borderRadius: 12,
      background: 'rgba(255,255,255,0.09)',
      border: '1px solid rgba(255,255,255,0.12)',
    },
    miniStatTop: {
      margin: 0,
      fontSize: 14,
      fontWeight: 850,
    },
    miniStatBottom: {
      margin: '2px 0 0',
      fontSize: 11,
      color: 'rgba(255,255,255,0.68)',
    },
    dashboardMock: {
      borderRadius: 24,
      background: '#ffffff',
      color: '#0f172a',
      overflow: 'hidden',
      border: '1px solid rgba(255,255,255,0.2)',
      boxShadow: '0 22px 55px rgba(0,0,0,0.22)',
    },
    mockHeader: {
      padding: '9px 13px',
      display: 'flex',
      justifyContent: 'space-between',
      borderBottom: '1px solid #e5e7eb',
      background: '#f8fafc',
      gap: 12,
      alignItems: 'center',
    },
    mockBody: {
      padding: 10,
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 9,
    },
    mockPanel: {
      border: '1px solid #e5e7eb',
      borderRadius: 13,
      padding: 11,
      background: '#ffffff',
    },
    mockPanelTitle: {
      margin: '0 0 8px',
      fontSize: 12,
      color: '#334155',
      fontWeight: 850,
    },
    calendarBlock: {
      padding: '7px 9px',
      borderRadius: 10,
      background: '#f0fdf4',
      color: '#166534',
      fontSize: 12,
      fontWeight: 800,
      marginBottom: 5,
    },
    teamSection: {
      padding: '0',
    },
    painGrid: {
      display: 'grid',
      gap: 6,
    },
    painItem: {
      display: 'flex',
      gap: 9,
      alignItems: 'flex-start',
      padding: '8px 11px',
      borderRadius: 12,
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
      color: '#475569',
      fontSize: 13,
      lineHeight: 1.4,
    },
    check: {
      width: 22,
      height: 22,
      minWidth: 22,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      background: '#dcfce7',
      color: '#166534',
      fontWeight: 900,
      fontSize: 13,
    },
    peopleCard: {
      padding: 14,
      borderRadius: 18,
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
    },
    avatarRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      padding: '8px 0',
      borderBottom: '1px solid #edf2f7',
    },
    avatarLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 11,
    },
    avatar: {
      width: 38,
      height: 38,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      background: '#f1f5f9',
      color: '#0f172a',
      fontWeight: 850,
      fontSize: 14,
    },
    personName: {
      margin: 0,
      color: '#0f172a',
      fontSize: 14,
      fontWeight: 850,
    },
    personRole: {
      margin: '2px 0 0',
      color: '#64748b',
      fontSize: 12,
    },
    synced: {
      color: '#047857',
      fontSize: 12,
      fontWeight: 850,
      background: '#ecfdf5',
      padding: '6px 8px',
      borderRadius: 999,
      whiteSpace: 'nowrap',
    },
    finalCta: {
      margin: '12px 0 24px',
      padding: '20px 22px',
      borderRadius: 20,
      textAlign: 'center',
      background:
        'linear-gradient(135deg, rgba(5, 150, 105, 0.10), rgba(59, 130, 246, 0.08)), #ffffff',
      border: '1px solid rgba(148, 163, 184, 0.24)',
      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
    },
    modalBackdrop: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 23, 42, 0.72)',
      backdropFilter: 'blur(5px)',
      zIndex: 2000,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      overflowY: 'auto',
      padding: '32px 16px 48px',
    },
    modalPanel: {
      position: 'relative',
      background: '#ffffff',
      borderRadius: 24,
      boxShadow: '0 32px 80px rgba(15, 23, 42, 0.28)',
      width: '100%',
      maxWidth: 1080,
      padding: '28px 24px 24px',
      marginTop: 'auto',
      marginBottom: 'auto',
    },
    modalClose: {
      position: 'absolute',
      top: 16,
      right: 16,
      width: 34,
      height: 34,
      borderRadius: '50%',
      border: '1px solid #e2e8f0',
      background: '#f8fafc',
      cursor: 'pointer',
      display: 'grid',
      placeItems: 'center',
      fontSize: 16,
      color: '#64748b',
      lineHeight: 1,
    },
    seeLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      marginTop: 14,
      color: '#059669',
      fontSize: 14,
      fontWeight: 700,
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      textDecoration: 'none',
    },
    responsiveCss: `
      @media (max-width: 980px) {
        .sc-hero,
        .sc-split,
        .sc-team {
          grid-template-columns: 1fr !important;
        }
        .sc-workflow,
        .sc-benefits {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }
      }
      @media (max-width: 640px) {
        .sc-shell {
          width: min(100% - 22px, 1120px) !important;
        }
        .sc-hero {
          padding-top: 34px !important;
          gap: 24px !important;
        }
        .sc-workflow,
        .sc-benefits,
        .sc-mock-body,
        .sc-mini-stats {
          grid-template-columns: 1fr !important;
        }
        .sc-split {
          padding: 24px 18px !important;
          border-radius: 24px !important;
        }
        .sc-preview-card {
          padding: 10px !important;
          border-radius: 22px !important;
        }
        .sc-task-row {
          grid-template-columns: 30px minmax(0, 1fr) !important;
        }
        .sc-date-pill {
          grid-column: 2 / -1;
          width: fit-content;
        }
      }
    `,
  };

  const workflowSteps = [
    {
      icon: '📄',
      title: 'Documents in',
      copy: 'Send offers, amendments, waivers, and closing documents.',
    },
    {
      icon: '✅',
      title: 'Tasks created',
      copy: 'SmartClose turns key dates into a clear closing plan.',
    },
    {
      icon: '👥',
      title: 'Team notified',
      copy: 'Agents, coordinators, and owners know what needs attention.',
    },
    {
      icon: '📅',
      title: 'Calendars synced',
      copy: 'Deadlines appear where your team already works.',
    },
  ];

  const painOutcomes = [
    'Less chasing through long email threads for the latest closing details.',
    'Everyone can see what is due, who owns it, and which property it belongs to.',
    'Deadlines are easier to manage before they become last-minute surprises.',
    'Teams stay organized without forcing agents into another heavy dashboard.',
  ];

  return (
    <div className="App" style={styles.page}>
      <style>{styles.responsiveCss}</style>

      <NavBar
        onSignInClick={openAuth}
        onProfileClick={onProfileClick}
        onBillingClick={onBillingClick}
        onSeatsUpdate={onSeatsUpdate}
      />
      <AuthModal showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal} />

      <main className="sc-shell" style={styles.shell}>
        <section className="sc-hero" style={styles.hero}>
          <div>
            <div style={styles.badge}>✨ AI-powered closing coordination</div>
            <h1 style={styles.h1}>Close every real estate transaction with confidence</h1>
            <p style={styles.heroText}>
              Turn transaction documents into organized tasks, deadlines, and synced calendars — automatically.
            </p>

            <div style={styles.ctaRow}>
              <button type="button" style={styles.primaryButton} onClick={openAuth}>
                Get Started
              </button>
            </div>
          </div>

          <div className="sc-preview-card" style={styles.previewCard} aria-label="SmartClose product preview">
            <div style={styles.previewInner}>
              <div style={styles.previewTop}>
                <div>
                  <p style={styles.address}>123 Maple Avenue</p>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>Closing plan preview</p>
                </div>
                <span style={styles.statusPill}>Ready to review</span>
              </div>

              <div style={styles.previewBody}>
                <div className="sc-task-row" style={styles.taskRow}>
                  <div style={styles.taskIcon}>✓</div>
                  <p style={styles.taskTitle}>Confirm financing condition</p>
                  <span className="sc-date-pill" style={styles.datePill}>May 24</span>
                </div>

                <div className="sc-task-row" style={styles.taskRow}>
                  <div style={styles.taskIcon}>✓</div>
                  <p style={styles.taskTitle}>Schedule property inspection</p>
                  <span className="sc-date-pill" style={styles.datePill}>May 27</span>
                </div>

                <div className="sc-task-row" style={styles.taskRow}>
                  <div style={styles.taskIcon}>✓</div>
                  <p style={styles.taskTitle}>Prepare closing deliverables</p>
                  <span className="sc-date-pill" style={styles.datePill}>Jun 02</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" style={styles.section}>
          <div style={styles.sectionHeader}>
            <p style={styles.eyebrow}>How it works</p>
            <h2 style={styles.h2}>Documents in. Tasks out. Calendars synced.</h2>
            <p style={styles.sectionCopy}>
              SmartClose brings structure to closing work without adding another complicated process.
            </p>
            <button type="button" style={styles.seeLink} onClick={() => setShowFlowModal(true)}>
              Full workflow diagram →
            </button>
          </div>

          <div className="sc-workflow" style={styles.workflow}>
            {workflowSteps.map((step) => (
              <article key={step.title} style={styles.workflowStep}>
                <div style={styles.workflowHeader}>
                  <div style={styles.workflowIcon}>{step.icon}</div>
                  <h3 style={styles.workflowTitle}>{step.title}</h3>
                </div>
                <p style={styles.workflowCopy}>{step.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <p style={styles.eyebrow}>Product overview</p>
            <h2 style={styles.h2}>Tasks, calendars, and your team — all in one place</h2>
            <p style={styles.sectionCopy}>See how SmartClose organizes every closing from documents to done.</p>
          </div>

          {/* Tab switcher */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 14 }}>
            {[['tasks', '✓ Tasks'], ['calendar', '📅 Calendar'], ['teams', '👥 Teams']].map(([key, label]) => (
              <button key={key} type="button" onClick={() => setActiveTab(key)} style={{
                padding: '6px 18px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                border: activeTab === key ? 'none' : '1px solid #e2e8f0',
                background: activeTab === key ? 'linear-gradient(135deg, #059669, #047857)' : '#ffffff',
                color: activeTab === key ? '#ffffff' : '#64748b',
                boxShadow: activeTab === key ? '0 3px 10px rgba(5,150,105,0.22)' : 'none',
                transition: 'all 0.15s ease',
              }}>{label}</button>
            ))}
          </div>

          {/* App window */}
          <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 12px 40px rgba(15,23,42,0.09)' }}>
            {/* Window chrome — matches app banner style */}
            <div style={{ background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)', borderBottom: '1px solid #c8d8d2', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', gap: 5 }}>
                {['#f87171', '#fbbf24', '#34d399'].map(c => <div key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c }} />)}
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#1e3a2f', marginLeft: 6 }}>
                SmartClose — {activeTab === 'tasks' ? 'Tasks' : activeTab === 'calendar' ? 'Calendar' : 'Teams'}
              </span>
            </div>

            {/* Tasks tab */}
            {activeTab === 'tasks' && (
              <div style={{ padding: 16, background: '#ffffff' }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                  {[['All Tasks', true], ['Due Soon', false], ['Completed', false]].map(([label, active]) => (
                    <span key={label} style={{ padding: '3px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: active ? '#ecfdf5' : '#f8fafc', color: active ? '#047857' : '#64748b', border: `1px solid ${active ? '#a7f3d0' : '#e2e8f0'}` }}>{label}</span>
                  ))}
                </div>
                {[
                  { address: '123 Maple Avenue', badge: 'Active', badgeBg: '#dcfce7', badgeText: '#166534', badgeBorder: '#bbf7d0', tasks: [{ title: 'Confirm financing condition', date: 'May 24', done: false }, { title: 'Schedule property inspection', date: 'May 27', done: false }, { title: 'Send offer letter', date: 'Done', done: true }] },
                  { address: '456 Oak Street', badge: 'In Review', badgeBg: '#fef3c7', badgeText: '#92400e', badgeBorder: '#fde68a', tasks: [{ title: 'Review purchase amendment', date: 'Jun 1', done: false }, { title: 'Prepare closing checklist', date: 'Jun 5', done: false }] },
                ].map(group => (
                  <div key={group.address} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>📍 {group.address}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: group.badgeText, background: group.badgeBg, padding: '2px 8px', borderRadius: 999, border: `1px solid ${group.badgeBorder}` }}>{group.badge}</span>
                    </div>
                    {group.tasks.map(t => (
                      <div key={t.title} style={{ display: 'grid', gridTemplateColumns: '18px 1fr auto', gap: 9, alignItems: 'center', padding: '6px 9px', borderRadius: 9, background: t.done ? '#f8fafc' : '#fff', border: '1px solid #f1f5f9', marginBottom: 4 }}>
                        <div style={{ width: 16, height: 16, borderRadius: 5, background: t.done ? '#dcfce7' : '#f0fdf4', border: `1.5px solid ${t.done ? '#86efac' : '#d1fae5'}`, display: 'grid', placeItems: 'center', fontSize: 9, color: '#059669' }}>{t.done ? '✓' : ''}</div>
                        <span style={{ fontSize: 12, color: t.done ? '#94a3b8' : '#0f172a', fontWeight: 600, textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: t.done ? '#94a3b8' : '#059669', background: t.done ? '#f1f5f9' : '#ecfdf5', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>{t.date}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Calendar tab */}
            {activeTab === 'calendar' && (
              <div style={{ padding: 16, background: '#ffffff' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>May 19–23, 2025</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button style={{ width: 26, height: 26, borderRadius: 7, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 13, color: '#64748b' }}>{'<'}</button>
                    <button style={{ width: 26, height: 26, borderRadius: 7, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 13, color: '#64748b' }}>{'>'}</button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 10 }}>
                  {[
                    { day: 'Mon', date: '19', events: [] },
                    { day: 'Tue', date: '20', events: [] },
                    { day: 'Wed', date: '21', events: [{ label: 'Financing — Maple Ave', bg: '#dcfce7', color: '#166534' }] },
                    { day: 'Thu', date: '22', events: [] },
                    { day: 'Fri', date: '23', events: [{ label: 'Inspection — Oak St', bg: '#dbeafe', color: '#1d4ed8' }] },
                  ].map(col => (
                    <div key={col.day}>
                      <div style={{ textAlign: 'center', marginBottom: 6 }}>
                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{col.day}</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{col.date}</div>
                      </div>
                      <div style={{ minHeight: 110, background: '#f8fafc', borderRadius: 10, padding: 6, border: '1px solid #f1f5f9' }}>
                        {col.events.map(ev => (
                          <div key={ev.label} style={{ background: ev.bg, color: ev.color, fontSize: 10, fontWeight: 700, padding: '5px 6px', borderRadius: 7, lineHeight: 1.35 }}>{ev.label}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{ label: 'Closing — Pine Rd', date: 'May 30', bg: '#fef3c7', color: '#92400e' }, { label: 'Amendment — Cedar Ln', date: 'Jun 2', bg: '#f3e8ff', color: '#6b21a8' }].map(ev => (
                    <div key={ev.label} style={{ flex: 1, background: ev.bg, borderRadius: 10, padding: '8px 11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: ev.color }}>{ev.label}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: ev.color, opacity: 0.75 }}>{ev.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Teams tab */}
            {activeTab === 'teams' && (
              <div style={{ padding: 16, background: '#ffffff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Your team</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#059669', background: '#ecfdf5', padding: '3px 10px', borderRadius: 999, border: '1px solid #a7f3d0' }}>4 connected</span>
                </div>
                {[
                  { init: 'LA', name: 'Lead Agent', sub: 'Sarah Mitchell', props: '3 properties', status: 'Synced', sBg: '#dcfce7', sColor: '#166534' },
                  { init: 'TC', name: 'Transaction Coordinator', sub: 'James R.', props: '2 properties', status: 'Synced', sBg: '#dcfce7', sColor: '#166534' },
                  { init: 'BO', name: 'Brokerage Owner', sub: 'Admin', props: '5 properties', status: 'Viewing', sBg: '#eff6ff', sColor: '#1d4ed8' },
                  { init: 'TM', name: 'Team Member', sub: 'Alex Chen', props: '1 property', status: 'Notified', sBg: '#f3e8ff', sColor: '#6b21a8' },
                ].map((m, i, arr) => (
                  <div key={m.name} style={{ display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: 10, alignItems: 'center', padding: '9px 0', borderBottom: i < arr.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f0fdf4', border: '1.5px solid #d1fae5', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, color: '#047857' }}>{m.init}</div>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{m.name}</p>
                      <p style={{ margin: '1px 0 0', fontSize: 11, color: '#64748b' }}>{m.sub} · {m.props}</p>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: m.sColor, background: m.sBg, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>{m.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="sc-team" style={styles.teamSection}>
          <div style={styles.sectionHeader}>
            <p style={styles.eyebrow}>Team collaboration</p>
            <h2 style={styles.h2}>Keep the whole closing team on the same page</h2>
            <p style={styles.sectionCopy}>
              SmartClose helps agents, coordinators, team members, and brokerage owners stay aligned from
              contract to close — without chasing every update manually.
            </p>
            <button
              type="button"
              style={{ ...styles.seeLink, display: 'inline-block', marginTop: 6 }}
              onClick={() => setShowWorkflowModal(true)}
            >
              Team routing diagram →
            </button>
          </div>

          <div style={{ ...styles.peopleCard, maxWidth: 520, margin: '14px auto 0' }}>
            {[
              ['LA', 'Lead Agent', 'Reviews and publishes updates'],
              ['TC', 'Transaction Coordinator', 'Tracks deadlines and follow-ups'],
              ['BO', 'Brokerage Owner', 'Keeps visibility across deals'],
            ].map(([initials, name, role], index) => (
              <div
                key={name}
                style={{
                  ...styles.avatarRow,
                  borderBottom: index === 2 ? 'none' : styles.avatarRow.borderBottom,
                }}
              >
                <div style={styles.avatarLeft}>
                  <div style={styles.avatar}>{initials}</div>
                  <div>
                    <p style={styles.personName}>{name}</p>
                    <p style={styles.personRole}>{role}</p>
                  </div>
                </div>
                <span style={styles.synced}>Aligned</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 10, textAlign: 'center' }} className="sc-pain-grid">
            {painOutcomes.map((item) => (
              <div key={item} style={{ ...styles.painItem, justifyContent: 'center' }}>
                <span style={styles.check}>✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section style={styles.finalCta}>
          <p style={styles.eyebrow}>Ready for a cleaner closing process?</p>
          <h2 style={styles.h2}>Close with less stress</h2>
          <p style={styles.sectionCopy}>
            Turn your next transaction document into an organized closing plan your team can actually follow.
          </p>
          <div style={{ ...styles.ctaRow, justifyContent: 'center', marginTop: 16 }}>
            <button type="button" style={styles.primaryButton} onClick={openAuth}>
              Get Started with SmartClose
            </button>
          </div>
        </section>
      </main>

      {showFlowModal && (
        <div
          style={styles.modalBackdrop}
          onClick={() => setShowFlowModal(false)}
        >
          <div
            style={styles.modalPanel}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              style={styles.modalClose}
              onClick={() => setShowFlowModal(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <p style={{ ...styles.eyebrow, marginBottom: 4 }}>AI Workflow</p>
            <p style={{ margin: '0 0 16px', fontSize: 15, color: '#475569' }}>
              How transaction documents are processed by SmartClose AI into tasks, calendar events, and team updates.
            </p>
            <WorkflowSVG />
          </div>
        </div>
      )}

      {showWorkflowModal && (
        <div
          style={styles.modalBackdrop}
          onClick={() => setShowWorkflowModal(false)}
        >
          <div
            style={styles.modalPanel}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              style={styles.modalClose}
              onClick={() => setShowWorkflowModal(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <p style={{ ...styles.eyebrow, marginBottom: 4 }}>Team Workflow</p>
            <p style={{ margin: '0 0 16px', fontSize: 15, color: '#475569' }}>
              How tasks and calendar events are routed based on who sends the document and their role.
            </p>
            <TeamWorkflowSVG />
          </div>
        </div>
      )}
    </div>
  );
}
